import autobind from 'class-autobind';

import TrendReduction from 'atg/api/TrendReduction';
import * as Utils from 'atg/utils/Utils';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { settings } from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import * as Text from 'shared/utils/Text';

import { createHelpIcon } from '../HelpIcon';
import BaseView from './BaseView';

const $ = window.$;

export default class Trend extends BaseView {
  constructor() {
    super(...arguments);
    autobind(this, Trend.prototype);
  }

  setup() {
    const api = this.getAPI();
    this.trendInformation = api.getTrendInformation(
      this.getTrendReduction().getTrendMinutes(),
    );
  }

  getGridClass() {
    return 'view-trend';
  }

  // ---------------------------------------- MENU ----------------------------------------

  getViewTitle() {
    return Text.get('view-trend', this.getNumber(Trend));
  }

  getMenuTitle() {
    return Text.get('view-trend', this.getNumber(Trend));
  }

  getMenuGroupTitle() {
    return Text.get('view-group-points');
  }

  getMenuAddItemTitle() {
    return Text.get('view-points-add');
  }

  getMenuMaxNumberOfViews() {
    return this.getTrendReductionSet().getMaxNumberOfTrendReductions();
  }

  getMenuTogglable() {
    return true;
  }

  getMenuDeletable() {
    return true;
  }

  // ---------------------------------------- TOOLBAR ----------------------------------------

  hasToolbar() {
    return true;
  }

  getToolbarContent() {
    return {
      getTrendMinutes: () => this.getTrendReduction().getTrendMinutes(),
      trendInformation: () => this.trendInformation,
    };
  }

  changeTrendTime(minutes) {
    const api = this.getAPI();
    api.getTrendInformation(minutes);
    this.getTrendReduction().setTrendMinutes(minutes);
    if (!this.getAPI().betInformation.getBetConfig().isLocked()) {
      this.getAPI().scheduleBetUpdate();
    }
  }

  // ---------------------------------------- GRID ----------------------------------------
  onRowWasUpdated() {}

  getGridCellInfo(position, rowCache) {
    const api = this.getAPI();

    let race;
    let betRow;
    let gameRow;
    let resultRow;
    let betConfig;

    if (rowCache) {
      race = rowCache.race;
      betRow = rowCache.betRow;
      gameRow = rowCache.gameRow;
      resultRow = rowCache.resultRow;
      betConfig = rowCache.betConfig;
    } else {
      // eslint-disable-next-line no-param-reassign
      rowCache = {};
      race = rowCache.race = api.raceInformation.getRace(position.row);
      betRow = rowCache.betRow = api.betInformation.getRow(position.row);
      gameRow = rowCache.gameRow = api.gameInformation.getRow(position.row);
      resultRow = rowCache.resultRow = api.resultInformation.getRow(
        position.row,
      );
      betConfig = rowCache.betConfig = api.betInformation.getBetConfig();
    }

    const startNumber = betRow
      ? betRow.getStartNumberAtPosition(position.column)
      : position.column + 1;

    const startIndex = startNumber - 1;

    const start = race.getStart(startIndex);

    const info = {
      rowCache: rowCache,
    };

    if (start) {
      info.number = start.getStartNumber();

      const horse = start.getHorse();
      if (horse) {
        info.horseName = horse.getName();
      }

      const jockey = start.getJockey();
      if (jockey) {
        info.jockeyTrainerName = jockey.getName();
      }

      const originalJockey = start.getOriginalJockey();
      if (originalJockey) {
        info.originalJockey = originalJockey.getName();
      }
    }

    if (
      resultRow &&
      settings.showRankingResults(getStore().getState()) &&
      betConfig.getShowResultRow(position.row)
    ) {
      info.winner = resultRow.isWinner(startIndex);
    }

    const gameRowCellInfo = this.getGameRowCellInfo(gameRow, startIndex);
    Object.assign(info, gameRowCellInfo);

    if (betRow) {
      const fragment = $('<div/>').addClass('trending-container');
      const currentSpan = $('<span/>').addClass('current');
      const trendSpan = $('<span/>').addClass('trending');
      let defaultPercent = betRow.getTrendPercentage(position.column);
      const periodValues = betRow.getPeriodTrend();
      let periodPercent;
      if (defaultPercent == null) {
        defaultPercent = gameRow.getMarkPercent(startIndex);
      }
      if (periodValues.length > 0) {
        periodPercent = periodValues[startIndex];
      }
      currentSpan.text(
        periodPercent ? Utils.compactPercent(periodPercent) : '-',
      );
      const currentTrend = betRow.getCurrentTrend();
      let trendPercent;
      if (currentTrend.length > 0) {
        trendPercent = currentTrend[startIndex];
      }
      if (trendPercent > 0) {
        trendPercent = `+${Utils.compactPercent(trendPercent, false)}`;
        trendSpan.css('color', '#306F1D');
      } else if (trendPercent < 0) {
        trendPercent = Utils.compactPercent(trendPercent, false);
        trendSpan.css('color', '#A72A17');
      } else {
        trendPercent = trendPercent
          ? Utils.compactPercent(trendPercent, false)
          : '-';
      }
      trendSpan.text(trendPercent);
      fragment.append(currentSpan, trendSpan);
      info.bottomBlue = fragment;
      betRow.setTrendPercentage(position.column, defaultPercent);
    }

    return info;
  }

  onGridCellTapped(position) {
    this.getGrid().highlightCell(position);

    const buttons = [
      {
        icon: 'button-last',
        onPress: this.onGridLastButtonPressed,
      },
      ...this.makeHorseButtons(position),
    ];

    this.getGrid().showCellButtons(position, buttons);
  }
  // ---------------------------------------- DATA ----------------------------------------

  getIndex() {
    return super.getIndex(Trend);
  }

  getTrendReduction() {
    const reductionSet = this.getTrendReductionSet();
    let reduction;

    if (reductionSet) {
      const index = this.getIndex();
      const numberOfReductions = reductionSet.getNumberOfTrendReductions();

      if (index < numberOfReductions) {
        reduction = reductionSet.getTrendReduction(index);
      }
    }

    if (!reduction) {
      reduction = new TrendReduction();
    }

    return reduction;
  }

  getTrendReductionSet() {
    return this.getAPI().betInformation.getReductionSet().getTrendReduction();
  }

  getItem() {
    return this.getTrendReduction();
  }

  addItem() {
    return this.getTrendReduction().addTrendReduction();
  }

  deleteItem() {
    const api = this.getAPI();
    const index = this.getIndex();

    this.getTrendReductionSet().deleteTrendReduction(index);

    const numberOfRows = api.betInformation.getNumberOfRows();
    for (let row = 0; row < numberOfRows; row++) {
      api.betInformation.getRow(row).deleteTrendPercentage();
    }

    this.getAPI().scheduleBetUpdate();
  }

  getNumberOfItems() {
    const set = this.getTrendReduction();
    return set ? set.getNumberOfTrendReductions() : 0;
  }

  // ---------------------------------------- BOTTOM INFO DELEGATE METHODS ----------------------------------------

  getBottomInfoHidden() {
    return false;
  }

  getBottomInfoContent() {
    const $conditions = (this.$conditions = StickyTemplate.jq(
      'bottom-conditions-trend-reduction',
      {
        /*** Section 1 ***/
        '%winners-negative-trend-header%': Text.get(
          'bottom-conditions-trend-negative-trend',
        ),
        /*** Section 2 ***/
        '%winners-positive-trend-header%': Text.get(
          'bottom-conditions-trend-positive-trend',
        ),
        /*** Section 3 ***/
        '%winners-increased-percent-header%': Text.get(
          'bottom-conditions-trend-increased-percent',
        ),
        /*** Section 4 ***/
        '%winners-decreased-percent-header%': Text.get(
          'bottom-conditions-trend-decreased-percent',
        ),
      },
    ));

    $conditions.find('.negative-trend h2').append(
      createHelpIcon({
        header: Text.get('help-trend-negative-winners-header'),
        body: Text.get('help-trend-negative-winners-body'),
      }),
    );

    $conditions.find('.positive-trend h2').append(
      createHelpIcon({
        header: Text.get('help-trend-positive-winners-header'),
        body: Text.get('help-trend-positive-winners-body'),
      }),
    );

    $conditions.find('.winners-increased-percent h2').append(
      createHelpIcon({
        header: Text.get('help-trend-increase-in-betdistribution-header'),
        body: Text.get('help-trend-increase-in-betdistribution-body'),
      }),
    );

    $conditions.find('.winners-decreased-percent h2').append(
      createHelpIcon({
        header: Text.get('help-trend-decrease-in-betdistribution-header'),
        body: Text.get('help-trend-decrease-in-betdistribution-body'),
      }),
    );

    // $conditions.find('form').event('submit', this.onFormSubmit);

    this.updateConditionsWinnersNegative();
    this.updateConditionsWinnersPositive();
    this.updateConditionsWinnersIncreasedPercent();
    this.updateConditionsWinnersDecreasedPercent();

    return $conditions;
  }

  // ---------------------------------------- WINNERS NEGATIVE TREND ----------------------------------------

  updateConditionsWinnersNegative() {
    const reduction = this.getTrendReduction();

    const $wrapper = this.$conditions.find('.negative-trend');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    const $row = StickyTemplate.jq(
      'bottom-conditions-winners-negative-trend-row',
      {
        '%Min%': Text.get('bottom-conditions-Min'),
        '%max%': Text.get('bottom-conditions-max'),
        '%with-negative-trend%': Text.get(
          'bottom-conditions-trend-winners-negative',
        ),
      },
    );

    this.addNumberOfRacesInput($row);

    $row
      .find('.min-points')
      .val(Utils.negativeBlankToValue(reduction.getMinNegativeTrend()));
    $row
      .find('.max-points')
      .val(Utils.negativeBlankToValue(reduction.getMaxNegativeTrend()));

    $rows.append($row);

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);

    this.attachNegativeBlankEvents($wrapper);
  }

  // ---------------------------------------- WINNERS POSITIVE TREND ----------------------------------------

  updateConditionsWinnersPositive() {
    const reduction = this.getTrendReduction();

    const $wrapper = this.$conditions.find('.positive-trend');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    const $row = StickyTemplate.jq(
      'bottom-conditions-winners-positive-trend-row',
      {
        '%Min%': Text.get('bottom-conditions-Min'),
        '%max%': Text.get('bottom-conditions-max'),
        '%with-positive-trend%': Text.get(
          'bottom-conditions-trend-winners-positive',
        ),
      },
    );

    this.addNumberOfRacesInput($row);

    $row
      .find('.from-points')
      .val(Utils.negativeBlankToValue(reduction.getMinPositiveTrend()));
    $row
      .find('.to-points')
      .val(Utils.negativeBlankToValue(reduction.getMaxPositiveTrend()));

    $rows.append($row);

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);

    this.attachNegativeBlankEvents($wrapper);
  }

  // ---------------------------------------- WINNERS INCREASED PERCENTAGE TREND ----------------------------------------

  updateConditionsWinnersIncreasedPercent() {
    const reduction = this.getTrendReduction();
    const numberOfRows = Math.max(
      1,
      reduction.getNumberOfWinnersIncreasedPercent(),
    );

    const $wrapper = this.$conditions.find('.winners-increased-percent');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    for (let n = 0; n < numberOfRows; n++) {
      const pointSum = reduction.getWinnersIncreasedPercent(n);

      const $row = StickyTemplate.jq(
        'bottom-conditions-winners-increased-percent-row',
        {
          '%Min%': Text.get('bottom-conditions-Min'),
          '%max%': Text.get('bottom-conditions-max'),
          '%with-increased%': Text.get(
            'bottom-conditions-trend-winners-increased',
          ),
          '%percent-point%': Text.get('bottom-conditions-trend-percent-point'),
          '%row%': n,
        },
      );

      this.addNumberOfRacesInput($row);

      if (pointSum) {
        $row
          .find('.from-points')
          .val(Utils.negativeBlankToValue(pointSum.getMin()));
        $row
          .find('.to-points')
          .val(Utils.negativeBlankToValue(pointSum.getMax()));
        $row
          .find('.percentage-point')
          .val(Utils.negativeBlankToValue(pointSum.getPercentagePoint()));
      }

      $rows.append($row);
    }

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);

    $wrapper
      .find('.button.plus')
      .reevent('down', this.onAddWinnersIncreasedPercentagePressed);
    $wrapper
      .find('.button.delete')
      .reevent('down', this.onDeleteIncreasedPercentagePressed);

    this.attachNegativeBlankEvents($wrapper);
  }

  onAddWinnersIncreasedPercentagePressed() {
    const reduction = this.getTrendReduction();

    if (reduction.getNumberOfWinnersIncreasedPercent() === 0) {
      reduction.addWinnersIncreasedPercent();
      // pointSum.setLegNumbers(this.getLegNumbers());
    }

    reduction.addWinnersIncreasedPercent();
    // pointSum.setLegNumbers(this.getLegNumbers());

    this.getAPI().scheduleBetUpdate();

    this.updateConditionsWinnersIncreasedPercent();

    const $row = this.$conditions.find(
      '.winners-increased-percent .rows .row:last-child',
    );
    $row.hide();
    $row.slideDown(300);
  }

  onDeleteIncreasedPercentagePressed(event) {
    const $target = $(event.currentTarget);
    const $row = $target.closest('.row');
    const index = $row.index();

    const reduction = this.getTrendReduction();

    reduction.deleteWinnersIncreasedPercent(index);
    this.getAPI().scheduleBetUpdate();

    if (reduction.getAllWinnersIncreasedPercent() < 1) {
      this.updateConditionsWinnersIncreasedPercent();
    } else {
      $row.slideUp(
        300,
        function () {
          this.updateConditionsWinnersIncreasedPercent();
        }.bind(this),
      );
    }
  }

  // ---------------------------------------- WINNERS DECREASED PERCENTAGE TREND ----------------------------------------

  updateConditionsWinnersDecreasedPercent() {
    const reduction = this.getTrendReduction();
    const numberOfRows = Math.max(
      1,
      reduction.getNumberOfWinnersDecreasedPercent(),
    );

    const $wrapper = this.$conditions.find('.winners-decreased-percent');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    for (let n = 0; n < numberOfRows; n++) {
      const pointSum = reduction.getWinnersDecreasedPercent(n);

      const $row = StickyTemplate.jq(
        'bottom-conditions-winners-decreased-percent-row',
        {
          '%Min%': Text.get('bottom-conditions-Min'),
          '%max%': Text.get('bottom-conditions-max'),
          '%with-decreased%': Text.get(
            'bottom-conditions-trend-winners-decreased',
          ),
          '%percent-point%': Text.get('bottom-conditions-trend-percent-point'),
          '%row%': n,
        },
      );

      this.addNumberOfRacesInput($row);

      if (pointSum) {
        $row
          .find('.from-points')
          .val(Utils.negativeBlankToValue(pointSum.getMin()));
        $row
          .find('.to-points')
          .val(Utils.negativeBlankToValue(pointSum.getMax()));
        $row
          .find('.percentage-point')
          .val(Utils.negativeBlankToValue(pointSum.getPercentagePoint()));
      }

      $rows.append($row);
    }

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);

    $wrapper
      .find('.button.plus')
      .reevent('down', this.onAddWinnersDecreasePercentagePressed);
    $wrapper
      .find('.button.delete')
      .reevent('down', this.onDeleteDecreasedPercentagePressed);

    this.attachNegativeBlankEvents($wrapper);
  }

  onAddWinnersDecreasePercentagePressed() {
    const reduction = this.getTrendReduction();

    if (reduction.getNumberOfWinnersDecreasedPercent() === 0) {
      reduction.addWinnersDecreasedPercent();
      // pointSum.setLegNumbers(this.getLegNumbers());
    }

    reduction.addWinnersDecreasedPercent();
    // pointSum.setLegNumbers(this.getLegNumbers());

    this.getAPI().scheduleBetUpdate();

    this.updateConditionsWinnersDecreasedPercent();

    const $row = this.$conditions.find(
      '.winners-decreased-percent .rows .row:last-child',
    );
    $row.hide();
    $row.slideDown(300);
  }

  onDeleteDecreasedPercentagePressed(event) {
    const $target = $(event.currentTarget);
    const $row = $target.closest('.row');
    const index = $row.index();

    const reduction = this.getTrendReduction();

    reduction.deleteWinnersDecreasedPercent(index);
    this.getAPI().scheduleBetUpdate();

    if (reduction.getAllWinnersDecreasedPercent() < 1) {
      this.updateConditionsWinnersDecreasedPercent();
    } else {
      $row.slideUp(
        300,
        function () {
          this.updateConditionsWinnersDecreasedPercent();
        }.bind(this),
      );
    }
  }

  // ---------------------------------------- FORM TO DATA ----------------------------------------

  getWinnersNegativeFormData(formData, reduction) {
    reduction.setMinNegativeTrend(
      Utils.valueToNegativeBlank(
        formData.minNegativeTrend ? formData.minNegativeTrend : -1,
      ),
    );
    reduction.setMaxNegativeTrend(
      Utils.valueToNegativeBlank(
        formData.maxNegativeTrend ? formData.maxNegativeTrend : -1,
      ),
    );
  }

  getWinnersPositiveFormData(formData, reduction) {
    reduction.setMinPositiveTrend(
      Utils.valueToNegativeBlank(
        formData.minPositiveTrend ? formData.minPositiveTrend : -1,
      ),
    );
    reduction.setMaxPositiveTrend(
      Utils.valueToNegativeBlank(
        formData.maxPositiveTrend ? formData.maxPositiveTrend : -1,
      ),
    );
  }

  getWinnersIncreasedPercentFormData(formData, reduction) {
    const numberOfFormRows = formData.winnersIncreasedPercent
      ? formData.winnersIncreasedPercent.length
      : 0;
    const numberOfBetDistributions =
      reduction.getNumberOfWinnersIncreasedPercent();

    for (let n = 0; n < numberOfFormRows; n++) {
      if (n >= numberOfBetDistributions) {
        reduction.addWinnersIncreasedPercent();
      }

      const winnersIncreasedPercent = reduction.getWinnersIncreasedPercent(n);
      const formWinnersIncreasedPercent = formData.winnersIncreasedPercent[n];

      winnersIncreasedPercent.setMin(
        Utils.valueToNegativeBlank(formWinnersIncreasedPercent.min || -1),
      );
      winnersIncreasedPercent.setMax(
        Utils.valueToNegativeBlank(formWinnersIncreasedPercent.max || -1),
      );
      winnersIncreasedPercent.setPercentagePoint(
        Utils.valueToNegativeBlank(
          formWinnersIncreasedPercent.percentagePoint || -1,
        ),
      );
    }

    /* Clean up */
    while (numberOfFormRows < numberOfBetDistributions) {
      reduction.deleteWinnersIncreasedPercent(
        reduction.getNumberOfWinnersIncreasedPercent() - 1,
      );
    }
  }

  getWinnersDecreasedPercentFormData(formData, reduction) {
    const numberOfFormRows = formData.winnersDecreasedPercent
      ? formData.winnersDecreasedPercent.length
      : 0;
    const numberOfBetDistributions =
      reduction.getNumberOfWinnersDecreasedPercent();

    for (let n = 0; n < numberOfFormRows; n++) {
      if (n >= numberOfBetDistributions) {
        reduction.addWinnersDecreasedPercent();
      }

      const winnersDecreasedPercent = reduction.getWinnersDecreasedPercent(n);
      const formWinnersDecreasedPercent = formData.winnersDecreasedPercent[n];

      winnersDecreasedPercent.setMin(
        Utils.valueToNegativeBlank(formWinnersDecreasedPercent.min || -1),
      );
      winnersDecreasedPercent.setMax(
        Utils.valueToNegativeBlank(formWinnersDecreasedPercent.max || -1),
      );
      winnersDecreasedPercent.setPercentagePoint(
        Utils.valueToNegativeBlank(
          formWinnersDecreasedPercent.percentagePoint || -1,
        ),
      );
    }

    /* Clean up */
    while (numberOfFormRows < numberOfBetDistributions) {
      reduction.deleteWinnersDecreasedPercent(
        reduction.getNumberOfWinnersDecreasedPercent() - 1,
      );
    }
  }

  onConditionsChanged() {
    const $form = this.$conditions.find('form');
    const formData = $form.formData();
    const reduction = this.getTrendReduction();

    this.getWinnersNegativeFormData(formData, reduction);
    this.getWinnersPositiveFormData(formData, reduction);
    this.getWinnersIncreasedPercentFormData(formData, reduction);
    this.getWinnersDecreasedPercentFormData(formData, reduction);

    this.getAPI().scheduleBetUpdate();
  }
}
